<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import AppNav from '@/components/AppNav.vue';
import Button from '@/components/ui/Button.vue';
import { purgeLegacyDeviceData } from '@/lib/legacyStorage';
import { useAuthStore } from '@/stores/auth';
import { useNotesStore } from '@/stores/notes';
import { usePrefsStore } from '@/stores/prefs';

const { t } = useI18n();
const auth = useAuthStore();
const prefs = usePrefsStore();
const notes = useNotesStore();

const online = ref(navigator.onLine);
const { needRefresh, updateServiceWorker } = useRegisterSW();

onMounted(async () => {
  purgeLegacyDeviceData();
  await auth.hydrate();
  await prefs.hydrate();

  // Push note edits held while offline as soon as there is a connection.
  if (auth.entitlements.serverSync) void notes.sync();

  window.addEventListener('online', () => {
    online.value = true;
    if (auth.entitlements.serverSync) void notes.sync();
  });
  window.addEventListener('offline', () => {
    online.value = false;
  });
});
</script>

<template>
  <div class="min-h-dvh">
    <!-- Signed out, the whole app is the sign-in gate at `/` — nav links to
         other routes would just bounce back here. -->
    <AppNav v-if="auth.isSignedIn" />

    <main class="sm:pt-2">
      <RouterView v-slot="{ Component }">
        <component :is="Component" />
      </RouterView>
    </main>

    <!-- Offline is a normal state here, not an error. Stated once, quietly. -->
    <p
      v-if="!online"
      class="pad-safe-bottom fixed inset-x-0 bottom-14 z-50 mx-auto w-fit rounded-full border border-ink-line bg-ink-raised px-4 py-1.5 text-xs text-muted sm:bottom-4"
      role="status"
    >
      {{ t('common.offline') }}
    </p>

    <div
      v-if="needRefresh"
      class="pad-safe-bottom fixed inset-x-0 bottom-14 z-50 mx-auto flex w-fit items-center gap-3 rounded-full border border-gold-dim/50 bg-ink-raised px-4 py-2 sm:bottom-4"
      role="status"
    >
      <span class="text-sm text-parchment">{{ t('settings.update') }}</span>
      <Button size="sm" @click="updateServiceWorker(true)">{{ t('settings.updateNow') }}</Button>
    </div>
  </div>
</template>
